import React, { memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert, Input, Select, Spin, Switch } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, tablet } from 'services/ui';
import { useDebouncedValue } from 'services/hooks';
import { Colors, SeniorityOptions, WorkModeOptions } from 'context';
import { ToastTypes } from 'atoms';
import { CareerVacancyCard, CareerVacancyDetails } from 'molecules';
import {
  formatSalaryRange,
  getCareerErrorText,
  useCareerVacancies,
  useMarkVacancyInteraction,
  useToaster,
} from 'core';
import { PATHS } from 'router/config';

import { useCareerAccessGuard } from './useCareerAccessGuard';

const INTERACTION_TOAST_KEY = 'careerVacancyInteraction';

/**
 * /career/vacancies — the member's vacancy feed (spec 0018 §B2).
 *
 * Filters (direction / work mode / seniority / debounced search) drive the
 * server-side query; every row's Save / Apply state is server truth
 * (`interaction`), and the mark mutation updates the normalized cache in place
 * so a click reflects without a refetch.
 */
export const CareerVacancies = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToaster();

  // Guard mirrors the other /career pages: a non-member hitting the URL is
  // bounced to 404, and the server refuses the data independently anyway.
  useCareerAccessGuard();

  const [directionId, setDirectionId] = useState<string | undefined>();
  const [workMode, setWorkMode] = useState<string | undefined>();
  const [seniority, setSeniority] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [savedOnly, setSavedOnly] = useState(false);
  // id открытой карточки; подробный вид читает её из уже загруженного списка,
  // поэтому отдельного запроса не нужно.
  const [detailsId, setDetailsId] = useState<string | null>(null);

  const debouncedSearch = useDebouncedValue(search);

  const { vacancies, directionOptions, loading, error, refetch } = useCareerVacancies({
    directionId,
    workMode,
    seniority,
    search: debouncedSearch,
    savedOnly,
  });

  const { markInteraction } = useMarkVacancyInteraction();
  // Per-vacancy in-flight set: a click on card B must not clear card A's
  // pending state, and both of a card's buttons stay disabled until its own
  // request resolves (guards the concurrent Save-then-Apply cache regression).
  const [pendingIds, setPendingIds] = useState<Set<string>>(new Set());

  const handleInteraction = useCallback(
    async (vacancyId: string, kind: 'saved' | 'applied') => {
      setPendingIds((prev) => new Set(prev).add(vacancyId));
      try {
        const result = await markInteraction(vacancyId, kind);
        if (!result.success) {
          addToast(INTERACTION_TOAST_KEY, {
            type: ToastTypes.danger,
            // По КОДУ, а не по тексту сервера: сообщение сервера русское.
            text: getCareerErrorText(t, result.errorCode),
          });
        }
      } catch {
        addToast(INTERACTION_TOAST_KEY, {
          type: ToastTypes.danger,
          text: t('careerCenter.vacancies.actionError'),
        });
      } finally {
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(vacancyId);
          return next;
        });
      }
    },
    [markInteraction, addToast, t],
  );

  const hasActiveFilters = Boolean(
    directionId || workMode || seniority || debouncedSearch.trim() || savedOnly,
  );
  const detailsVacancy = vacancies.find((vacancy) => vacancy.id === detailsId) || null;

  return (
    <PageWrapper>
      <BackLink type="button" onClick={() => navigate(PATHS.careerCenter)}>
        ← {t('careerCenter.vacancies.back')}
      </BackLink>

      <PageHeader>
        <PageTitle>{t('careerCenter.vacancies.title')}</PageTitle>
        <PageInfo>{t('careerCenter.vacancies.info')}</PageInfo>
      </PageHeader>

      <FiltersBar>
        {directionOptions.length ? (
          <FilterSelect
            allowClear
            value={directionId}
            placeholder={t('careerCenter.vacancies.filters.direction')}
            options={directionOptions}
            onChange={(value) => setDirectionId((value as string) || undefined)}
          />
        ) : null}

        <FilterSelect
          allowClear
          value={workMode}
          placeholder={t('careerCenter.vacancies.filters.workMode')}
          options={WorkModeOptions}
          onChange={(value) => setWorkMode((value as string) || undefined)}
        />

        <FilterSelect
          allowClear
          value={seniority}
          placeholder={t('careerCenter.vacancies.filters.seniority')}
          options={SeniorityOptions}
          onChange={(value) => setSeniority((value as string) || undefined)}
        />

        <SearchInput
          allowClear
          value={search}
          placeholder={t('careerCenter.vacancies.filters.search')}
          onChange={(event) => setSearch(event.target.value)}
        />

        <SavedFilter>
          <Switch size="small" checked={savedOnly} onChange={setSavedOnly} />
          {t('careerCenter.vacancies.filters.savedOnly')}
        </SavedFilter>
      </FiltersBar>

      {error ? (
        <Alert type="error" showIcon message={t('careerCenter.vacancies.loadError')} />
      ) : loading && !vacancies.length ? (
        <SpinWrapper>
          <Spin size="large" />
        </SpinWrapper>
      ) : vacancies.length ? (
        <VacancyList>
          {vacancies.map((vacancy) => (
            <CareerVacancyCard
              key={vacancy.id}
              title={vacancy.title}
              companyName={vacancy.companyName}
              location={vacancy.location}
              workMode={vacancy.workMode}
              seniority={vacancy.seniority}
              employmentType={vacancy.employmentType}
              salary={formatSalaryRange(
                vacancy.salaryMin,
                vacancy.salaryMax,
                vacancy.salaryCurrency,
              )}
              description={vacancy.description}
              directionTitles={vacancy.directions.map((direction) => direction.title)}
              skillNames={vacancy.skillNames}
              applyUrl={vacancy.applyUrl}
              expiresAt={vacancy.expiresAt}
              interaction={vacancy.interaction}
              actionLoading={pendingIds.has(vacancy.id)}
              onSave={() => handleInteraction(vacancy.id, 'saved')}
              onOpenDetails={() => setDetailsId(vacancy.id)}
            />
          ))}
        </VacancyList>
      ) : (
        <EmptyState>
          {hasActiveFilters
            ? t('careerCenter.vacancies.emptyFiltered')
            : t('careerCenter.vacancies.empty')}
          {hasActiveFilters ? (
            <ResetButton type="button" onClick={() => refetch()}>
              {t('careerCenter.vacancies.refresh')}
            </ResetButton>
          ) : null}
        </EmptyState>
      )}

      <CareerVacancyDetails
        vacancy={detailsVacancy}
        onClose={() => setDetailsId(null)}
        onSave={() => (detailsVacancy ? handleInteraction(detailsVacancy.id, 'saved') : undefined)}
        actionLoading={Boolean(detailsVacancy && pendingIds.has(detailsVacancy.id))}
      />
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const BackLink = styled.button`
  margin-top: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}

  &:hover {
    color: ${Colors.main};
  }
`;

const PageHeader = styled.div`
  margin: 16px 0 24px 0;
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const FiltersBar = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  width: 100%;
  margin-bottom: 24px;

  ${tablet} {
    grid-template-columns: repeat(2, 1fr);
  }

  ${desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const FilterSelect = styled(Select)`
  width: 100%;
` as typeof Select;

const SearchInput = styled(Input)`
  width: 100%;
`;

const VacancyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 48px 0;
  text-align: center;
  color: ${Colors.gray_dark};
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.regular })}
`;

const ResetButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: ${Colors.main};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const SavedFilter = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${Colors.gray_dark};
  cursor: pointer;
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;
